import {
  BarChart3, Briefcase, CalendarCheck, ClipboardList, FileSignature, Home, LayoutDashboard, Map, Megaphone, MessageSquare,
  Package, Palette, PlugZap, Receipt, Settings, ShieldCheck, ShoppingBag, Sparkles, Users, Wallet, Wrench, Building2, ScrollText,
  Handshake, Inbox, CreditCard, Gift, Contact, WalletCards, ListChecks, FileText,
} from "lucide-react";
import { t } from "@/i18n";

export const MAIN_NAV = [
  { to: "/", key: "home", icon: LayoutDashboard, end: true, tone: "mint" },
  { to: "/finances", key: "finances", icon: Wallet, tone: "butter" },
  { to: "/foyer", key: "foyer", icon: Home, tone: "peach" },
  { to: "/market", key: "market", icon: ShoppingBag, tone: "rose" },
  { to: "/cagnottes", key: "pots", icon: Gift, tone: "lilac", soon: "pots" },
  { to: "/compte-en-ligne", key: "account", short: "Compte", icon: WalletCards, tone: "sky", soon: "account" },
  { to: "/proches", key: "friends", short: "Amis", icon: Contact, tone: "mint" },
  { to: "/assistant", key: "assistant", icon: Sparkles, tone: "sky" },
];

const proHome = { to: "/pro", key: "pro-home", label: "Tableau de bord", short: "Accueil", icon: LayoutDashboard, end: true, tone: "mint" };
const item = (slug, label, icon, tone, feature) => ({ to: `/pro/${slug}`, key: `pro-${slug}`, label, icon, tone, feature });

export const PRO_NAV = {
  repairer: [proHome, item("demandes", "Demandes & devis", ClipboardList, "sky", 0), item("rendez-vous", "Interventions", CalendarCheck, "butter", 1), item("facturation", "Facturation", Receipt, "rose", 2)],
  installer: [proHome, item("demandes", "Demandes & devis", ClipboardList, "sky", 0), item("rendez-vous", "Interventions", CalendarCheck, "butter", 1), item("facturation", "Facturation", Receipt, "rose", 2)],
  manufacturer: [proHome, item("catalogue", "Catalogue", Package, "sky", 0), item("sav", "SAV", MessageSquare, "peach", 1), item("parc", "Parc installé", Map, "butter", 2), item("campagnes", "Campagnes", Megaphone, "rose", 3)],
  insurer: [proHome, item("assures", "Assurés actifs", Users, "sky", 0), item("sinistres", "Sinistres", FileSignature, "peach", 1), item("statistiques", "Statistiques", BarChart3, "butter", 2)],
  bank: [proHome, item("licences", "Clients", ShieldCheck, "sky", 0), item("dossiers", "Dossiers partagés", FileSignature, "peach", 1), item("rendez-vous", "Rendez-vous", CalendarCheck, "butter", 2)],
};

export const ADMIN_NAV = [
  { to: "/admin", key: "admin-overview", label: "Vue d'ensemble", short: "Accueil", icon: LayoutDashboard, end: true, tone: "mint" },
  { to: "/admin/utilisateurs", key: "admin-users", label: "Utilisateurs", icon: Users, tone: "sky", platform: "admin" },
  { to: "/admin/organisations", key: "admin-orgs", label: "Organisations", icon: Building2, tone: "butter", platform: "admin" },
  { to: "/admin/partenaires", key: "admin-partners", label: "Partenaires", icon: Handshake, tone: "lilac", platform: "admin" },
  { to: "/admin/liste-attente", key: "admin-waitlist", label: "Liste d'attente", icon: ListChecks, tone: "mint", platform: "admin" },
  { to: "/admin/offres", key: "admin-offers", label: "Offres & projets", icon: Gift, tone: "lilac", platform: "admin" },
  { to: "/admin/juridique", key: "admin-legal", label: "Pages juridiques", icon: FileSignature, tone: "sky", platform: "admin" },
  { to: "/admin/audit", key: "admin-audit", label: "Audit", icon: ScrollText, tone: "peach", platform: "admin" },
  { to: "/admin/integrations", key: "admin-integrations", label: "Intégrations", icon: PlugZap, tone: "rose" },
];

const biz = (slug, label, icon, tone, short) => ({ to: slug ? `/entreprise/${slug}` : "/entreprise", key: `biz-${slug || "home"}`, label, short, icon, tone, end: !slug });
export const BIZ_NAV = [
  biz("", "Tableau de bord", LayoutDashboard, "mint", "Accueil"),
  biz("depenses", "Dépenses & budgets", Wallet, "butter", "Dépenses"),
  biz("factures", "Dettes & créances", FileText, "lilac", "Factures"),
  biz("fournisseurs", "Fournisseurs & factures", Building2, "sky", "Fournisseurs"),
  biz("equipements", "Équipements & matériel", Wrench, "mint", "Matériel"),
  biz("charges", "Charges récurrentes", Receipt, "peach", "Charges"),
  biz("cartographie", "Cartographie fournisseurs", Map, "lilac", "Carto"),
  biz("contrats", "Assurances & prévoyance", ShieldCheck, "sky", "Contrats"),
  biz("entreprise", "Mon entreprise", Building2, "rose", "Entreprise"),
];

export const PARTNER_NAV = [
  { to: "/partenaires", key: "partner-leads", label: "Leads disponibles", short: "Leads", icon: Inbox, tone: "mint", end: true },
  { to: "/partenaires/achetes", key: "partner-bought", label: "Mes leads", short: "Mes leads", icon: Handshake, tone: "sky" },
  { to: "/partenaires/paiements", key: "partner-payments", label: "Paiements", icon: CreditCard, tone: "butter" },
];

const PERSONAL = ["/", "/finances", "/foyer", "/market", "/cagnottes", "/compte-en-ligne", "/proches", "/assistant"];

export const workspaceOf = (user, pathname) => {
  if (pathname.startsWith("/admin")) return "admin";
  if (pathname.startsWith("/partenaires") || (user.partner && !user.households.length)) return "partner";
  if (pathname === "/pro" || pathname.startsWith("/pro/")) return "pro";
  if (pathname.startsWith("/entreprise")) return "business";
  if (PERSONAL.includes(pathname) || user.households.length) return "personal";
  if (user.companies?.length) return "business";
  if (user.organizations.length) return "pro";
  return user.platform_role ? "admin" : "personal";
};

const isSuper = (u) => ["admin", "demo_admin"].includes(u.platform_role);

export const navFor = (user, pathname) => {
  const ws = workspaceOf(user, pathname);
  if (ws === "admin") return ADMIN_NAV.filter((i) => !i.platform || (i.platform === "admin" ? isSuper(user) : i.platform === user.platform_role));
  if (ws === "pro") return PRO_NAV[user.organizations[0]?.type] || [proHome];
  if (ws === "business") return BIZ_NAV;
  if (ws === "partner") return PARTNER_NAV;
  return MAIN_NAV;
};

export const labelOf = (i) => i.label || t(`nav.${i.key}`);

export const WS_HOME = { personal: "/", pro: "/pro", business: "/entreprise", partner: "/partenaires", admin: "/admin" };

export const workspaceLinks = (user) => [
  user.households.length ? { to: "/", key: "ws-personal", label: "Espace personnel", icon: Home, tone: "peach", ws: "personal" } : null,
  user.organizations.length ? { to: "/pro", key: "ws-pro", label: user.organizations[0].name, icon: Briefcase, tone: "lilac", ws: "pro" } : null,
  user.companies?.length ? { to: "/entreprise", key: "ws-business", label: (user.companies.find((c) => c.id === localStorage.getItem("cashmy.company")) || user.companies[0]).name, icon: Building2, tone: "butter", ws: "business" } : null,
  user.partner ? { to: "/partenaires", key: "ws-partner", label: user.partner.company, icon: Handshake, tone: "lilac", ws: "partner" } : null,
  user.platform_role ? { to: "/admin", key: "ws-admin", label: "Console CASHMY", icon: ShieldCheck, tone: "sky", ws: "admin" } : null,
].filter(Boolean);

export const utilityNav = (user) => [
  isSuper(user) ? { to: "/brand", key: "brand", icon: Palette, tone: "rose" } : null,
  { to: "/settings", key: "settings", icon: Settings, tone: "mint" },
].filter(Boolean);

export const findProItem = (user, slug) => (PRO_NAV[user.organizations[0]?.type] || []).find((i) => i.to === `/pro/${slug}`);
